import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkSheet } from "@egds/react-core/sheet";
import { Viewport, ViewSmall, ViewLarge } from "@shared-ui/viewport-context";
import { UitkEGMaps, UitkEGMapsType, UitkEGMapsRef } from "@egds/react-core/map";
import { UitkToolbar, UitkToolbarProps } from "@egds/react-core/toolbar";
import { inject } from "mobx-react";
import { EGMap, FeatureObject, EGMapConfiguration, CoordinatesArray, Coordinates } from "@eg-maps/core";
import { FullScreenMapProps, MapMarker, MarkerType } from "./typings";
import { MapMarkerCard } from "./cards/MapMarkerCard";
import { MobileMenu, DesktopMenu } from "./menus/Menus";
import { addAriaLabelsToMarker, convertToEgMapsMarkers } from "./DestinationDiscoveryMapUtils";
import {
  Action,
  FlexTrackingInfo,
  sendImmediateClickstreamTrackEvent,
} from "src/components/utility/analytics/FlexAnalyticsUtils";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
const MAX_NUMBER_OF_LANDMARKS = 10;

const FullScreenMap = inject("analytics")((props: FullScreenMapProps) => {
  const { destination, toggleMapDialog, context, pois, markers } = props;
  const [showMobileNav, setShowMobileNav] = React.useState(false);
  const [selectedCardContent, setSelectedCardContent] = React.useState<MapMarker>();
  const [selectedFeature, setSelectedFeature] = React.useState<FeatureObject>();
  const mapRef = React.useRef<UitkEGMapsRef>(null);
  const centerMapOnCustomFeatureAndSelectIt = React.useRef<boolean>(true);
  const [egMap, setEgMap] = React.useState<EGMap>();
  const { formatText } = useLocalization();
  const triggerRef = React.createRef<HTMLElement>();
  const track = useClickTracker();

  const egMapMarkers = convertToEgMapsMarkers(markers, formatText);

  const onMarkerSelectionChanged = React.useCallback(
    (event) => {
      const { action, feature } = event;
      switch (action) {
        case "clearSelection": {
          setSelectedCardContent(undefined);
          break;
        }
        case "select": {
          const selectedCardContent: MapMarker | undefined = markers.find(
            (marker) => marker.cardContent.id === feature.id
          );

          if (selectedCardContent) {
            const featureType = selectedCardContent.cardContent.type === MarkerType.HOTEL ? "property" : "place";
            const selectedFeature: FeatureObject = {
              id: selectedCardContent.cardContent.id,
              name: selectedCardContent.cardContent.title,
              lat: selectedCardContent.position.lat,
              lng: selectedCardContent.position.lng,
              type: featureType,
              status: "unknown",
              qualifiers: selectedCardContent.qualifiers,
            };

            addAriaLabelsToMarker(selectedFeature, formatText);
            setSelectedFeature(selectedFeature);

            const flexTrackingInfo: FlexTrackingInfo = {
              moduleName: selectedCardContent.cardContent.rfrr,
              action: Action.CLICK,
            };

            sendImmediateClickstreamTrackEvent(flexTrackingInfo, track);
          }

          setSelectedCardContent(selectedCardContent);
          break;
        }
        default:
          break;
      }
    },
    [selectedCardContent]
  );

  const toggleMobileNav = () => {
    setShowMobileNav(!showMobileNav);
  };

  const hideCard = () => {
    setSelectedCardContent(undefined);
  };

  const landmarks =
    pois &&
    pois.slice(0, MAX_NUMBER_OF_LANDMARKS).map((poi: MapMarker) => {
      const updateCard = () => {
        toggleMobileNav();

        const event = {
          action: "select",
          feature: {
            id: poi.cardContent.id,
          },
        };

        onMarkerSelectionChanged(event);

        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: `${poi.cardContent.rfrr}.Menu`,
          action: Action.CLICK,
        };

        sendImmediateClickstreamTrackEvent(flexTrackingInfo, track);
      };

      return {
        id: poi.cardContent.id,
        title: poi.cardContent.title,
        description: poi.cardContent.description,
        onClick: updateCard,
      };
    });

  const initialViewport = React.useMemo(
    () => egMapMarkers?.map((f) => [f.lat, f.lng] as Coordinates) as CoordinatesArray,
    []
  );

  const updateMarkers = React.useCallback(
    (map) => {
      const bounds = map.pushFeatureData(egMapMarkers, "replace");
      if (bounds) map.fitBounds(bounds.sw, bounds.ne);
    },
    [egMapMarkers]
  );

  const onInit = React.useCallback(
    (map) => {
      updateMarkers(map);
    },
    [updateMarkers]
  );

  const beforeInit = React.useCallback(
    (map) => {
      setEgMap(map);
      map.addSelectionChangeListener(onMarkerSelectionChanged);
    },
    [onMarkerSelectionChanged]
  );

  const mapFeaturesToCoordinates = (features: FeatureObject[]): CoordinatesArray => {
    return features.map((feature) => [feature.lat || 0, feature.lng || 0]);
  };

  const doCenterMapOnCustomFeatureAndSelectIt = (
    allFeatures: FeatureObject[],
    customFeature: FeatureObject,
    map?: EGMap
  ) => {
    const coordinates = mapFeaturesToCoordinates(customFeature ? [...allFeatures, customFeature] : allFeatures);
    map?.boundsContaining(coordinates, true).then(({ sw, ne }) => {
      map.fitBounds(sw, ne, true);
      if (selectedFeature) {
        map.selectFeature(customFeature.id, customFeature.type);
      }
    });
  };

  React.useEffect(() => {
    if (centerMapOnCustomFeatureAndSelectIt.current && selectedFeature) {
      doCenterMapOnCustomFeatureAndSelectIt(egMapMarkers, selectedFeature, egMap);
      centerMapOnCustomFeatureAndSelectIt.current = false;
    }

    // we need to enable center on feature next time one is added
    centerMapOnCustomFeatureAndSelectIt.current = true;
  }, [selectedFeature]);

  const toolbarAttributes: UitkToolbarProps = {
    toolbarTitle: `${destination.name}, ${destination.country}`,
    navigationContent: {
      navIconLabel: formatText("destinationDiscoveryMap.button.close"),
      onClick: toggleMapDialog("close"),
    },
  };

  const egMapsConfig: EGMapConfiguration = {
    googleMaps: {
      gmapsAddConfig: {
        mapId: "466ac5e186f00481",
        center: {
          lat: destination.latitude,
          lng: destination.longitude,
        },
        zoom: 14,
      },
    },
    locale: context.locale,
  };

  const mapComponent = (
    <UitkEGMaps
      accessToken="AIzaSyABOFQNLMiTVN0AG3eWNmspVfVC8gNda68"
      beforeInit={beforeInit}
      features={egMapMarkers}
      initialViewport={initialViewport}
      innerRef={mapRef}
      mapConfig={egMapsConfig}
      mapType={UitkEGMapsType.GOOGLE}
      onInit={onInit}
    >
      {selectedCardContent && (
        <MapMarkerCard
          content={selectedCardContent?.cardContent}
          showCard={Boolean(selectedCardContent)}
          onClose={hideCard}
        />
      )}
    </UitkEGMaps>
  );

  React.useEffect(() => {
    const flexTrackingInfo: FlexTrackingInfo = {
      moduleName: "TG.LP.Dest.Map.close",
      action: Action.CLICK,
    };

    return () => sendImmediateClickstreamTrackEvent(flexTrackingInfo, track);
  }, []);

  return (
    <UitkSheet type="full" triggerRef={triggerRef} isVisible className="DestinationDiscoveryMap">
      <UitkToolbar {...toolbarAttributes} />
      <Viewport>
        <ViewSmall>
          <>
            {landmarks && (
              <div id="dx-mobile-card" className={`mobile-nav-card ${showMobileNav && "mobile-nav-card--reveal"}`}>
                <MobileMenu
                  landmarks={landmarks}
                  toggle={toggleMobileNav}
                  isOpen={showMobileNav}
                  title={formatText("destinationDiscoveryMap.title.mobile", destination.name)}
                />
              </div>
            )}
            {mapComponent}
          </>
        </ViewSmall>
        <ViewLarge>
          <>
            {landmarks && (
              <DesktopMenu
                landmarks={landmarks}
                title={formatText("destinationDiscoveryMap.title.desktop", destination.name)}
              />
            )}
            {mapComponent}
          </>
        </ViewLarge>
      </Viewport>
    </UitkSheet>
  );
});

export default FullScreenMap;
